<template>
	<div class="resource-planning-projects-popover">
		<ResourcePlanningProjectsPopoverSkeleton v-if="loading" />

		<div v-else>
			<header class="resource-planning-projects-popover__header">
				<h4
					v-for="({ text }, index) in columnHeader"
					:key="text + index"
					class="resource-planning-projects-popover__heading"
				>
					{{ text }}
				</h4>
			</header>
			<ul
				ref="scrollableElementRef"
				class="resource-planning-projects-popover__list"
			>
				<li
					v-for="(
						{
							project,
							fixedBookedDuration,
							tentativeBookedDuration,
							totalBookedDuration,
							utilization,
						},
						index
					) in formattedUserUtilizationData"
					:key="project?.id ?? index"
					class="resource-planning-projects-popover__project-item"
					:class="{
						'resource-planning-projects-popover__project-item--with-scrollbar':
							isScrollbarVisible,
					}"
				>
					<div
						v-if="project?.permissions?.canView || project?.name"
						class="resource-planning-projects-popover__project-wrapper"
					>
						<SuiIcon
							:name="getProjectIconName(project)"
							:fill="project?.color ?? ''"
						/>
						<div
							class="resource-planning-projects-popover__project-details"
						>
							<SuiTruncatedText
								as="a"
								class="resource-planning-projects-popover__project-name"
								:href="
									getProjectBookingTabUrl(project?.id ?? '')
								"
								:text="project?.name"
							/>
							<SuiTruncatedText
								as="a"
								class="resource-planning-projects-popover__project-contact"
								:href="
									getProjectClientUrl(
										project?.company?.id ?? ''
									)
								"
								:text="project?.company?.name"
							/>
						</div>
					</div>
					<p
						v-else
						class="resource-planning-projects-popover__no-permission"
					>
						{{ $t('private') }}
					</p>
					<span
						v-if="isFixedBookingsVisible"
						class="resource-planning-projects-popover__number"
					>
						{{ fixedBookedDuration }}
						{{ $t('heatmap-time-booked') }}
					</span>
					<span
						v-if="isTentativeBookingsVisible"
						class="resource-planning-projects-popover__number"
					>
						{{ tentativeBookedDuration }}
						{{ $t('heatmap-time-booked') }}
					</span>
					<div class="resource-planning-projects-popover__total">
						<strong
							class="resource-planning-projects-popover__highlighted"
						>
							{{ totalBookedDuration }}
							{{ $t('heatmap-time-booked') }}
						</strong>
						<span
							v-if="showCapacityInfo"
							class="resource-planning-projects-popover__utilization"
						>
							{{ `${utilization}%` }}
						</span>
					</div>
				</li>
			</ul>
			<footer class="resource-planning-projects-popover__footer">
				<div
					class="resource-planning-projects-popover__footer-first-column"
				>
					<SuiProgressBar
						v-if="showCapacityInfo"
						size="compact"
						class="resource-planning-projects-popover__progressbar"
					>
						<SuiProgressBarArea
							v-for="progress in progressPercentage"
							:key="progress.class"
							v-tooltip="progress.label"
							:percentage="progress.percent"
							:class="`resource-planning-projects-popover__progressbar-item--${progress.class}`"
							:aria-label="progress.label"
						/>
					</SuiProgressBar>
					<div
						class="resource-planning-projects-popover__footer-text-wrapper"
					>
						<p
							class="resource-planning-projects-popover__footer-text"
						>
							{{ summaryText }}
						</p>
						<SuiIcon
							v-if="isOnlyRoleEntityType"
							v-tooltip="
								$t(
									'resource-planning-popover__placeholder-tooltip'
								)
							"
							name="icon-info-16"
						/>
					</div>
				</div>
				<strong
					v-if="isFixedBookingsVisible"
					class="resource-planning-projects-popover__highlighted"
				>
					{{ shortDuration(totalFixedDuration)[0].value }}
					{{ $t('heatmap-time-booked') }}
				</strong>
				<strong
					v-if="isTentativeBookingsVisible"
					class="resource-planning-projects-popover__highlighted"
				>
					{{ shortDuration(totalTentativeDuration)[0].value }}
					{{ $t('heatmap-time-booked') }}
				</strong>
				<strong class="resource-planning-projects-popover__highlighted">
					{{ shortDuration(totalDuration)[0].value }}
					{{ $t('heatmap-time-booked') }}
				</strong>
			</footer>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, toRefs, onMounted, onUnmounted, ref } from 'vue';
import {
	SuiIcon,
	SuiProgressBar,
	SuiProgressBarArea,
	SuiTruncatedText,
} from '@developers/scoro-ui';
import { useFormatter, useTranslation } from '@/composables';
import { BOOKING_TYPE_FILTER } from '../../constants';
import { closePopOver } from '../ResourcePlanningGantt/ResourcePlanningGanttUtils';
import { shortDuration } from '@/utils/formatting';
import {
	type User,
	UtilizationGroupBy,
	useResourcePlanningProjectsPopoverQuery,
	type Role,
	type Project,
} from '@/generated/graphql';
import { type BookingTypeFilter } from '../../types';
import ResourcePlanningProjectsPopoverSkeleton from './ResourcePlanningProjectsPopoverSkeleton.vue';
import {
	getProjectBookingTabUrl,
	getProjectClientUrl,
	getProjectIconName,
} from '@/features/Project/helpers';

export type ResourcePlanningProjectsPopoverProps = {
	filterValue: BookingTypeFilter;
	roleIds: Role['id'][];
	userIds: User['id'][];
	startDate: Date;
	endDate: Date;
	hasTentativeBookingsFeature: boolean;
	filterHash?: string;
	module?: string;
};

const props = defineProps<ResourcePlanningProjectsPopoverProps>();

const scrollableElementRef = ref<HTMLUListElement | null>(null);

const { dateToMysqlString } = useFormatter();
const { t } = useTranslation();

const {
	filterValue,
	userIds,
	roleIds,
	hasTentativeBookingsFeature,
	startDate,
	endDate,
	filterHash,
	module,
} = toRefs(props);

const { loading, result } = useResourcePlanningProjectsPopoverQuery(
	{
		input: {
			userIds: userIds.value ?? [],
			roleIds: roleIds.value ?? [],
			groupBy: UtilizationGroupBy.Project,
			startDate: dateToMysqlString(startDate.value),
			endDate: dateToMysqlString(endDate.value),
			filterHash: filterHash.value,
			module: module.value,
		},
	},
	{
		fetchPolicy: 'no-cache',
		notifyOnNetworkStatusChange: true,
	}
);

const utilizationData = computed(() => result.value?.UserUtilization);

const isOnlyRoleEntityType = computed(
	() => roleIds.value.length > 0 && userIds.value.length === 0
);

const isFixedBookingsVisible = computed(() => {
	if (isOnlyRoleEntityType.value) {
		return false;
	}

	return hasTentativeBookingsFeature.value
		? filterValue.value !== BOOKING_TYPE_FILTER.TENTATIVE
		: true;
});

const isTentativeBookingsVisible = computed(() =>
	hasTentativeBookingsFeature.value
		? filterValue.value !== BOOKING_TYPE_FILTER.FIXED
		: false
);

const totalDurationBasedOnFilter = (
	fixedBookedDuration: number,
	tentativeBookedDuration: number
) => {
	if (filterValue.value === BOOKING_TYPE_FILTER.FIXED) {
		return fixedBookedDuration;
	}

	if (filterValue.value === BOOKING_TYPE_FILTER.TENTATIVE) {
		return tentativeBookedDuration;
	}

	return fixedBookedDuration + tentativeBookedDuration;
};

const totalFixedDuration = computed(() => {
	if (!isFixedBookingsVisible.value || !utilizationData.value) {
		return 0;
	}

	let totalDuration = 0;

	utilizationData.value?.forEach((resourceUtilization) => {
		totalDuration += resourceUtilization.utilizations.reduce(
			(acc, utilization) => acc + utilization.fixedBookedDuration,
			0
		);
	});

	return totalDuration;
});

const totalTentativeDuration = computed(() => {
	if (!isTentativeBookingsVisible.value || !utilizationData.value) {
		return 0;
	}

	let totalDuration = 0;

	utilizationData.value?.forEach((resourceUtilization) => {
		totalDuration += resourceUtilization.utilizations.reduce(
			(acc, utilization) => acc + utilization.tentativeBookedDuration,
			0
		);
	});

	return totalDuration;
});

const totalDuration = computed(
	() => totalFixedDuration.value + totalTentativeDuration.value
);

const totalAvailability = computed<number>(() => {
	if (!utilizationData.value) {
		return 0;
	}

	return utilizationData.value[0].totalAvailability;
});

const totalUtilization = computed(() => {
	if (!utilizationData.value) {
		return 0;
	}

	return Math.round((totalDuration.value / totalAvailability.value) * 100);
});

const formattedUserUtilizationData = computed(() => {
	return utilizationData.value
		?.map((resourceUtilization) => {
			return resourceUtilization.utilizations
				.filter(
					({ fixedBookedDuration, tentativeBookedDuration }) =>
						fixedBookedDuration > 0 || tentativeBookedDuration > 0
				)
				.map(
					({
						fixedBookedDuration,
						tentativeBookedDuration,
						project,
					}) => {
						const totalDuration = totalDurationBasedOnFilter(
							fixedBookedDuration,
							tentativeBookedDuration
						);

						return {
							fixedBookedDuration:
								shortDuration(fixedBookedDuration)[0].value,
							tentativeBookedDuration: shortDuration(
								tentativeBookedDuration
							)[0].value,
							totalBookedDuration:
								shortDuration(totalDuration)[0].value,
							utilization: Math.round(
								(totalDuration /
									resourceUtilization.totalAvailability) *
									100
							),
							userId: resourceUtilization?.userId,
							roleId: resourceUtilization?.roleId,
							project: project as Project,
						};
					}
				);
		})
		.flat();
});

const columnHeader = computed(() =>
	[
		{ text: t('project'), isVisible: true },
		{
			text: hasTentativeBookingsFeature.value
				? t('resource-booking-type__fixed')
				: t('resource_planning_booked_duration'),
			isVisible: isFixedBookingsVisible.value,
		},
		{
			text: t('resource-booking-type__tentative'),
			isVisible: isTentativeBookingsVisible.value,
		},
		{ text: t('resource-planning-popover__utilization'), isVisible: true },
	].filter(({ isVisible }) => isVisible)
);

const gridTemplateColumns = computed(() => {
	if (!isFixedBookingsVisible.value || !isTentativeBookingsVisible.value) {
		return 'minmax(14rem, 1fr) repeat(2, 10rem)';
	}

	return 'minmax(14rem, 1fr) repeat(3, 10rem)';
});

const popOverWidth = computed(() => {
	if (!isFixedBookingsVisible.value || !isTentativeBookingsVisible.value) {
		return '53rem';
	}

	return '64rem';
});

const showCapacityInfo = computed(
	() => !isOnlyRoleEntityType.value || totalAvailability.value > 0
);

const overBookedDuration = computed(() =>
	totalUtilization.value > 100
		? totalDuration.value - totalAvailability.value
		: 0
);

const availableDuration = computed(() =>
	totalUtilization.value < 100
		? totalAvailability.value - totalDuration.value
		: 0
);

const makePercent = (value: number) =>
	value ? Math.ceil((value / totalDuration.value) * 100) : 0;

const progressPercentage = computed(() =>
	[
		{
			percent: makePercent(totalFixedDuration.value),
			label: `${t('resource-booking-type__fixed')}: ${
				shortDuration(totalFixedDuration.value)[0].value
			} ${t('heatmap-time-booked')}`,
			class: 'fixed',
		},
		{
			percent: makePercent(totalTentativeDuration.value),
			label: `${t('resource-booking-type__tentative')}: ${
				shortDuration(totalTentativeDuration.value)[0].value
			} ${t('heatmap-time-booked')}`,
			class: 'tentative',
		},
		{
			percent: makePercent(overBookedDuration.value),
			label: `${t('resource-planning-popover__overbooked')}: ${
				shortDuration(overBookedDuration.value)[0].value
			} ${t('heatmap-time-booked')}`,
			class: 'overbooked',
		},
		{
			percent: makePercent(availableDuration.value),
			label: `${t('resource-planning-popover__available')}: ${
				shortDuration(availableDuration.value)[0].value
			} ${t('heatmap-time-booked')}`,
			class: 'available',
		},
	].filter((progress) => progress.percent)
);

const summaryText = computed(() => {
	const percentage = `${totalUtilization.value}%`;
	const hours = `${shortDuration(totalAvailability.value)[0].value} ${t(
		'heatmap-time-booked'
	)}`;

	if (!showCapacityInfo.value) {
		return t('resource-planning-popover__no-capacity');
	}

	return t('resource_planning_popover_booked_percentage', {
		percentage,
		hours,
	});
});

const isScrollbarVisible = computed(() => {
	if (scrollableElementRef.value) {
		return (
			scrollableElementRef.value.scrollHeight >
			scrollableElementRef.value.clientHeight
		);
	}

	return false;
});

const createBackgroundElement = () => {
	const background = document.createElement('div');

	background.className = 'resource-booking-projects-popover__background';
	background.setAttribute(
		'data-testid',
		'resource-booking-projects-popover-background'
	);
	background.addEventListener('click', () => {
		closePopOver();
	});

	document.body.appendChild(background);
};

const removeBackgroundElement = () => {
	const background = document.querySelector(
		'.resource-booking-projects-popover__background'
	);

	background?.remove();
};

onMounted(() => {
	createBackgroundElement();
});

onUnmounted(() => {
	removeBackgroundElement();
});
</script>

<style lang="scss" scoped>
%grid-layout {
	display: grid;
	grid-template-columns: v-bind(gridTemplateColumns);
	gap: 3rem;
}

.resource-planning-projects-popover {
	min-width: v-bind(popOverWidth);
	max-width: v-bind(popOverWidth);

	&__header {
		@extend %grid-layout;

		padding: 2rem 2.5rem 1rem;
		border-bottom: 1px solid $neutral-20;
	}

	&__heading {
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		max-width: 100%;
		color: $neutral-70;
		font-size: 1.375rem;
		font-weight: $scoro-fw-normal;
		line-height: 2rem;

		&:not(:first-of-type) {
			place-self: center end;
		}
	}

	&__list {
		margin: 0;
		max-height: 30vh;
		overflow-y: auto;
	}

	&__project-item {
		@extend %grid-layout;

		padding: 1.5rem 0;
		margin: 0 2.5rem;

		&--with-scrollbar {
			margin-right: 1.5rem;
		}

		&:not(:last-of-type) {
			border-bottom: 1px solid $neutral-20;
		}
	}

	&__project-wrapper {
		display: flex;
		gap: 1rem;
	}

	&__project-details,
	&__total {
		display: flex;
		flex-direction: column;
		width: 100%;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	&__project-name {
		color: $neutral-90;
		font-size: 1.5rem;
		font-weight: $scoro-fw-semi-bold;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		cursor: pointer;

		&:hover {
			color: $blue-60;
		}
	}

	&__project-contact,
	&__utilization {
		color: $neutral-70;
		font-size: 1.375rem;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__project-contact {
		cursor: pointer;

		&:hover {
			color: $blue-60;
		}
	}

	&__no-permission {
		color: $neutral-90;
	}

	&__utilization {
		text-align: right;
	}

	&__number {
		color: $neutral-90;
		text-align: right;
		font-size: 1.5rem;
	}

	&__highlighted {
		color: $neutral-90;
		text-align: right;
		font-size: 1.5rem;
		font-weight: $scoro-fw-semi-bold;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	&__footer {
		@extend %grid-layout;

		padding: 1rem 2.5rem 1.75rem;
		border-top: 1px solid $neutral-20;
	}

	&__footer-first-column {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	&__progressbar {
		margin-top: 0.75rem;
	}

	&__progressbar-item {
		&--fixed {
			background-color: $purple-40;
		}

		&--tentative {
			background-color: $purple-30;
		}

		&--overbooked {
			background-color: $red-30;
		}

		&--available {
			background-color: $neutral-20;
		}
	}

	&__footer-text-wrapper {
		display: flex;
		gap: 0.5rem;
	}

	&__footer-text {
		font-size: 1.5rem;
		color: $neutral-80;
	}
}
</style>

<style lang="scss">
.resource-booking-projects-popover__background {
	z-index: z('modal-popup');
	inset: 0;
	position: fixed;
}
</style>
